import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Per-deploy cache key for every asset the scene templates resolve through
// Remotion's staticFile() — fonts and SFX.
//
// Those live in public/ with stable, unhashed names (CRA only hashes
// build/static/), and server.js caches the whole build dir for a year, so a
// deploy that changes a font changes the bytes but not the URL and the
// browser keeps the old one until a hard refresh. Prefixing the static base
// with the build id makes every such URL new on every deploy; server.js strips
// the /v/<id> segment back off before serving the file.
//
// It MUST be a path segment. staticFile() builds its URL as
// `${window.remotion_staticBase}/${path}`, so a query-string base would
// produce "/?v=abc/fonts/…" — the id would land before the path and the
// request would 404.
//
// Set here rather than in the preview pane because it has to be in place
// before ANY <Player> mounts, and unset in development (no id is generated for
// `npm start`), where the plain /fonts/… paths are what we want.
const BUILD_ID = process.env.REACT_APP_BUILD_ID;
if (BUILD_ID) {
  window.remotion_staticBase = `/v/${BUILD_ID}`;
}

const container = document.getElementById('root');

// scripts/prerender.js stamps window.__PRERENDERED__ into the HTML it writes, so
// a prerendered document is HYDRATED (React adopts the existing DOM) while a
// plain SPA load is rendered from scratch. Calling createRoot on prerendered
// markup would throw the server-rendered content away and repaint, which is the
// flash people blame on the prerenderer.
//
// This only works because App resolves its auth state synchronously (see
// readStoredSession in App.jsx) — with the old useEffect-based loading gate the
// first client render was a spinner and could never match the prerendered page.
//
// ...but ONLY for a logged-out visitor. Pages are prerendered once, logged out,
// so a visitor with a stored session renders the authenticated tree on the first
// pass and cannot possibly match. React notices, discards the markup and
// re-renders — correct output, but it reports the mismatch as minified errors
// #418/#423 in the console, which buries real errors. Detecting the case up
// front and rendering fresh gets the identical result without the noise.
const hasStoredSession = (() => {
  try {
    return Boolean(localStorage.getItem('token') && localStorage.getItem('user'));
  } catch {
    return false; // localStorage blocked (privacy mode) — treat as logged out
  }
})();

if (window.__PRERENDERED__ && container.hasChildNodes() && !hasStoredSession) {
  ReactDOM.hydrateRoot(container, <App />);
} else {
  ReactDOM.createRoot(container).render(<App />);
}
